// App.jsx — GoldenScope
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './styles.css';

import store from './utils/store';
import { contractsDb, profileDb, paalDb, libraryDb, cmsDb, templatesDb, localSettings, sharedLibraryDb, bootDb, agencyDb } from './utils/store';
import supabase from './utils/supabaseClient';
import Auth from './components/Auth';
import PendingApproval from './components/PendingApproval';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import CMOnboardingFlow from './components/CMOnboardingFlow';
import CMDashboard from './components/CMDashboard';
import {
  STAGES, DEFAULT_PAAL, DEFAULT_PROFILE, DEFAULT_CONTRACT,
  REF_DOCS, ALL_LIB,
  uid, today, fmtDate, fmtMoney, fmtSize, dgsCheck,
  nextExLabel, buildContractListEntry, todayStr as getTodayStr,
} from './utils/constants';

import { ToastContainer, toast } from './components/UI';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import {
  NewContractModal, ImportContractModal,
  StageModal, DeleteConfirmModal, AddAmendmentModal,
} from './components/Modals';

// Lazy-loaded tab components
import StageTab from './components/tabs/StageTab';
import DraftTab from './components/tabs/DraftTab';
import HandoffTab from './components/tabs/HandoffTab';
import BidEvalTab from './components/tabs/BidEvalTab';
import ArchiveView from './components/views/ArchiveView';
import SupervisorView from './components/views/SupervisorView';
import VendorLibraryView from './components/views/VendorLibraryView';
import SurveyPage from './components/SurveyPage';
// ExhibitsTab and AttachmentsTab replaced by AssemblyTab and DocumentsTab
import AmendmentsTab from './components/tabs/AmendmentsTab';
import EmailsTab from './components/tabs/EmailsTab';
import STD215Tab from './components/tabs/STD215Tab';
import STD213Tab from './components/tabs/STD213Tab';
import AssemblyTab from './components/tabs/AssemblyTab';
import DocumentsTab from './components/tabs/DocumentsTab';
import CollaborationPanel from './components/CollaborationPanel';
import OnboardingFlow from './components/OnboardingFlow';
import ContractCelebration from './components/ContractCelebration';
import AgencyAdminPanel from './components/AgencyAdminPanel';
import VaultTab from './components/tabs/VaultTab';
import RemindersTab from './components/tabs/RemindersTab';

// Settings views
import ProfileView from './components/views/ProfileView';
import PAALView from './components/views/PAALView';
import LibraryView from './components/views/LibraryView';
import SettingsView from './components/views/SettingsView';
import AnalyticsView from './components/views/AnalyticsView';
import TemplateLibraryView from './components/views/TemplateLibraryView';
import CMRosterView from './components/views/CMRosterView';

// ─── Anthropic API proxy helpers ────────────────────────────────────────────
// All AI calls now route through the GoldenScope Worker proxy at api.goldenscope.app.
// The proxy validates the user's Supabase session, forwards to Anthropic,
// and logs every call to the ai_usage_log Supabase table.
//
// The `apiKey` parameter is retained for backward compatibility but is IGNORED —
// authentication is now done via the user's Supabase JWT. The proxy holds the
// real Anthropic key as a secret.
const PROXY_URL = 'https://api.goldenscope.app';

// Get the current user's Supabase access token for authenticating to the proxy
async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in. Please log in to continue.');
  return session.access_token;
}

async function callAI(apiKey, { system, messages, model = 'claude-sonnet-4-6', max_tokens = 2000, betas = [], tag = 'untagged', contract_id = null }) {
  const token = await getAuthToken();
  const res = await fetch(PROXY_URL + '/ai', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    body: JSON.stringify({ model, max_tokens, system, messages, betas, tag, contract_id }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  // Truncation detection — Anthropic returns stop_reason="max_tokens" when the
  // output was cut off mid-generation. We surface this as a console warning
  // (always) and a toast (since callAI is module-level, we import toast
  // directly). The text returned will still be the partial output — caller
  // can use it, but knowing it was cut short is critical for SOW drafts that
  // look complete but actually drop their last 1-2 sections.
  if (data.stop_reason === 'max_tokens') {
    console.warn('[callAI truncation] tag=' + tag + ' model=' + model + ' max_tokens=' + max_tokens + ' — output was cut off. Increase max_tokens or split the request.');
    try {
      toast.warn?.('Response was cut off (max_tokens reached for ' + tag + '). Output may be incomplete.');
    } catch (e) { /* toast not available in this context */ }
  }
  return data.content.map(b => b.text || '').join('');
}

// Upload a file to Anthropic Files API (via proxy) — returns file_id
async function uploadToAnthropicFiles(apiKey, base64, filename) {
  const token = await getAuthToken();
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: 'application/pdf' });
  const formData = new FormData();
  formData.append('file', blob, filename || 'document.pdf');
  const res = await fetch(PROXY_URL + '/files', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + token,
      // Content-Type is auto-set by the browser to include the multipart boundary
    },
    body: formData,
  });
  const data = await res.json();
  if (data.error) throw new Error('Files API: ' + data.error.message);
  return data.id;
}

// Get a valid Anthropic file_id for a doc — re-uploads from base64 if expired (>28 days)
async function getValidFileId(apiKey, doc) {
  const uploadedAt = doc.anthropicFileUploadedAt ? new Date(doc.anthropicFileUploadedAt) : null;
  const daysSinceUpload = uploadedAt ? (Date.now() - uploadedAt.getTime()) / (1000 * 60 * 60 * 24) : 999;
  if (doc.anthropicFileId && daysSinceUpload < 28) {
    return { fileId: doc.anthropicFileId, refreshed: false };
  }
  if (!doc.base64) return null;
  const fileId = await uploadToAnthropicFiles(apiKey, doc.base64, doc.name);
  return { fileId, refreshed: true, uploadedAt: new Date().toISOString() };
}

// ─── Main App ──────────────────────────────────────────────────────────────
export default function App() {
  // ── Core state ────────────────────────────────────────────────────────
  const [booting, setBooting]   = useState(true);
  const dataLoadedRef = React.useRef(false);
  const [session, setSession]     = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showAgencyAdmin, setShowAgencyAdmin] = useState(false);
  const [memberRoles, setMemberRoles] = useState({});
  const [authReady, setAuthReady]   = useState(false);
  const [userStatus, setUserStatus] = useState('active');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [superAdminView, setSuperAdminView] = useState('dashboard');
  const [view, setView] = useState('dashboard');
  const [tab, setTab] = useState('stage');
  const [darkMode, setDarkMode] = useState(false);

  // ── CM state ──────────────────────────────────────────────────────────
  const [showCMOnboarding, setShowCMOnboarding] = useState(false);
  const [cmProfile, setCmProfile] = useState(null);

  // ── Data state ────────────────────────────────────────────────────────
  const [contracts, setContracts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [contract, setContract] = useState(null);
  const [paal, setPaal] = useState(DEFAULT_PAAL);
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [library, setLibrary] = useState({});
  const [cms, setCms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [agencyDocTemplate, setAgencyDocTemplate] = useState(null); // agency-wide default doc profile
  const [libLoading, setLibLoading] = useState({});
  const [apiKey] = useState(process.env.REACT_APP_ANTHROPIC_KEY || 'proxy-managed');

  // ── Modal state ───────────────────────────────────────────────────────
  const [showNewModal, setShowNewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [pendingStage, setPendingStage] = useState(null);

  // ── Feature state ─────────────────────────────────────────────────────
  const [importLoading, setImportLoading] = useState(false);
  const [autosave, setAutosave] = useState('idle');

  // ── Refs ──────────────────────────────────────────────────────────────
  const contractSaveTimer = useRef(null);
  const contractListSaveTimer = useRef(null);
  const pendingContractRef = useRef(null);
  const generateScmContextRef = useRef(null);

  // ── Dark mode ─────────────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    store.set('settings:darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const trigger = contract?.pennyDrafts?.triggerScmRefresh;
    if (!trigger || !generateScmContextRef.current) return;
    const expectedKey = (contract.procMethod || 'unspecified') + '_' + (contract.isIT || 'no');
    const triggerKey = trigger.split('_').slice(0, 2).join('_');
    if (triggerKey !== expectedKey) return;
    updateC({ pennyDrafts: { ...(contract.pennyDrafts || {}), triggerScmRefresh: null } });
    generateScmContextRef.current();
  }, [contract?.pennyDrafts?.triggerScmRefresh]); // eslint-disable-line

  useEffect(() => {
    const handler = () => {
      if (contractSaveTimer.current && pendingContractRef.current) {
        contractsDb.save(pendingContractRef.current);
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []); // eslint-disable-line

  // ── Auth state listener ────────────────────────────────────────────────
  useEffect(() => {
    async function checkSession(session) {
      setSession(session);
      if (session?.user) {
        const { data: sa } = await supabase.rpc('is_super_admin');
        setIsSuperAdmin(!!sa);
        // Don't read role or status from auth metadata — it's often stale
        // Both are set authoritatively from user_profiles.* in the boot effect
      } else {
        setIsSuperAdmin(false);
        setUserStatus(null);
      }
      setAuthReady(true);
    }

    supabase.auth.getSession().then(({ data: { session } }) => checkSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      checkSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Boot: load all data ───────────────────────────────────────────────
  useEffect(() => {
    async function boot() {
      if (!session) { setBooting(false); return; }
      if (dataLoadedRef.current) return;
      dataLoadedRef.current = true;
      try {
        const userId = session.user.id;
        setCurrentUserId(userId);
        if (!userId) { setBooting(false); return; }

        // Load profile
        const { data: prof } = await supabase
          .from('user_profiles').select('*').eq('id', userId).single();

        let agencyId = null;
        if (prof) {
          agencyId = prof.agency_id;
          setProfile({
            contactName: prof.contact_name || '',
            contactTitle: prof.contact_title || '',
            contactPhone: prof.contact_phone || '',
            contactEmail: prof.contact_email || '',
            agencyName: prof.agency_name || '',
            agencyDivision: prof.agency_division || '',
            agencyBillingCode: prof.agency_billing_code || '',
            accountingOfficerName: prof.accounting_officer_name || '',
            accountingOfficerTitle: prof.accounting_officer_title || '',
            accountingOfficerPhone: prof.accounting_officer_phone || '',
            accountingOfficerEmail: prof.accounting_officer_email || '',
            signatoryName: prof.signatory_name || '',
            signatoryTitle: prof.signatory_title || '',
            signatoryPhone: prof.signatory_phone || '',
            signatoryEmail: prof.signatory_email || '',
            signatoryAddress: prof.signatory_address || '',
            signatoryCity: prof.signatory_city || '',
            signatoryZip: prof.signatory_zip || '',
            supervisorName: prof.supervisor_name || '',
            supervisorTitle: prof.supervisor_title || '',
            supervisorPhone: prof.supervisor_phone || '',
            supervisorEmail: prof.supervisor_email || '',
            id: prof.id,
            agencyId: prof.agency_id,
            scoAgencyCode: prof.sco_agency_code || '3355',
            unionHrName: prof.union_hr_name || '',
            unionHrEmail: prof.union_hr_email || '',
            unionLrName: prof.union_lr_name || '',
            unionLrEmail: prof.union_lr_email || '',
            unionCcEmails: prof.union_cc_emails || '',
            role: prof.role || 'member',
          });

          // Set userStatus from DB — authoritative source after admin approval
          setUserStatus(prof.status || 'pending');

          // Stamp last_seen_at — non-blocking so it doesn't slow boot.
          // The Super Admin dashboard reads this to show when each user
          // was last active. Fires on every app load (login or returning
          // session), so it captures ongoing usage, not just sign-in events.
          supabase.from('user_profiles')
            .update({ last_seen_at: new Date().toISOString() })
            .eq('id', userId).then(() => {});

          // ── Load CM profile if user is a contract manager ──────────────
          if (prof.role === 'contract_manager' && prof.cm_profile_id) {
            const { data: cmProf } = await supabase
              .from('cm_profiles')
              .select('*')
              .eq('id', prof.cm_profile_id)
              .single();
            if (cmProf) {
              setCmProfile(cmProf);
              // Show onboarding if profile is still incomplete
              const isIncomplete = !cmProf.title && !cmProf.phone && !cmProf.division;
              if (isIncomplete) setShowCMOnboarding(true);
            }
          }
        }

        // Load PAAL
        if (agencyId) {
          const { data: pa } = await supabase
            .from('agency_paal').select('*').eq('agency_id', agencyId).single();
          if (pa) setPaal({
            nonIT: pa.non_it || 50000,
            it: pa.it || 200000,
            tier: pa.tier || '1',
            effectiveDate: pa.effective_date || '',
            expirationDate: pa.expiration_date || '',
            notes: pa.notes || '',
            uploaded: pa.uploaded || false,
            file: pa.file_name ? { name: pa.file_name, size: pa.file_size, date: pa.file_date } : null,
          });
        }

        // Load CMs
        const { data: cmsData } = await supabase
          .from('user_cms').select('*').eq('user_id', userId);
        if (cmsData?.length) setCms(cmsData.map(cm => ({
          ...cm,
          agencyName: cm.agency_name || '',
          addresses: cm.addresses?.length
            ? cm.addresses
            : cm.address
              ? [{ label: 'Main Office', street: cm.address, city: '', state: 'CA', zip: '' }]
              : [],
        })));

        // Load templates (RLS auto-filters: own + agency-scope)
        const tmplData = await templatesDb.listVisible();
        if (tmplData?.length) setTemplates(tmplData);

        // Load agency-wide doc template profile (fallback when no specific
        // template matches at draft time)
        try {
          const agencyDt = await agencyDb.getDocTemplate();
          if (agencyDt) setAgencyDocTemplate(agencyDt);
        } catch (e) { console.warn('agencyDb.getDocTemplate failed:', e); }

        // Load contracts
        const [ownedResult, memberResult] = await Promise.all([
          supabase.from('contracts').select('*').order('updated_at', { ascending: false }),
          supabase.from('contract_members').select('contract_id, role').eq('user_id', userId),
        ]);
        if (ownedResult.error) console.error('Failed to load owned contracts:', ownedResult.error);
        if (memberResult.error) console.error('Failed to load contract memberships:', memberResult.error);

        const memberContractIds = (memberResult.data || []).map(m => m.contract_id);
        const memberRolesMap = Object.fromEntries((memberResult.data || []).map(m => [m.contract_id, m.role]));
        setMemberRoles(memberRolesMap);
        let sharedContractsData = [];
        if (memberContractIds.length > 0) {
          const { data: sharedData } = await supabase
            .from('contracts').select('*').in('id', memberContractIds);
          sharedContractsData = sharedData || [];
        }

        const ownedIds = new Set((ownedResult.data || []).map(c => c.id));
        const allContractsRaw = [
          ...(ownedResult.data || []),
          ...sharedContractsData.filter(c => !ownedIds.has(c.id)),
        ].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));

        setContracts((allContractsRaw || []).map(c => ({
          id: c.id, title: c.title, contractNum: c.contract_num || '',
          ownerId: c.owner_id,
          isShared: c.owner_id !== userId,
          description: c.description,
          budget: c.budget, isIT: c.is_it, procMethod: c.proc_method,
          category: c.category, notes: c.notes,
          stage: c.stage, stageHistory: c.stage_history || [],
          s215: c.s215 || {}, exhibits: c.exhibits || [],
          exhibitNumbering: c.exhibit_numbering || 'alpha',
          attachments: c.attachments || [], amendments: c.amendments || [],
          reminders: c.reminders || [], documents: c.documents || {},
          referenceDocs: c.reference_docs || [],
          assignedCmId: c.assigned_cm_id,
          exemption: c.exemption || {},
          otaExemption: c.ota_exemption || {},
          dvbe: c.dvbe || {},
          pennyDrafts: c.penny_drafts || {},
          handoff: c.handoff || {},
          bidEval: c.bid_eval || {},
          calEProcure: c.cal_eprocure || {},
          archived: c.archived || false,
          archivedAt: c.archived_at || null,
          analystName: c.analyst_name || '',
          analystEmail: c.analyst_email || '',
          sbDvbeCertNum: c.sb_dvbe_cert_num || '',
          contractorVendorId: c.contractor_vendor_id || null,
          contractNum: c.contract_num || '',
          general_attachments: c.general_attachments || [],
          std213: c.std213 || {},
          createdAt: c.created_at?.split('T')[0],
          updatedAt: c.updated_at?.split('T')[0],
        })));

        // Dark mode from localStorage
        const savedDark = localSettings.get('darkMode');
        if (savedDark !== null) setDarkMode(!!savedDark);

        // Load library metadata
        const [sharedKeys, agencyKeys] = await Promise.all([
          supabase.from('shared_library').select('key, name, file_size').then(r => r.data || []),
          agencyId
            ? supabase.from('agency_library').select('key, name, file_size').eq('agency_id', agencyId).then(r => r.data || [])
            : Promise.resolve([]),
        ]);
        const libObj = {};
        sharedKeys.forEach(({ key, name, file_size }) => {
          libObj[key] = { name, size: file_size, loaded: false, shared: true };
        });
        agencyKeys.forEach(({ key, name, file_size }) => {
          libObj[key] = { name, size: file_size, loaded: false, shared: false };
        });
        setLibrary(libObj);

      } catch (e) {
        console.error('Boot error:', e);
      } finally {
        setBooting(false);
      }
    }
    boot();
  }, [session]); // eslint-disable-line

  // ── Computed ──────────────────────────────────────────────────────────
  const s215 = contract?.s215 || {};
  const dgsResult = contract
    ? (contract.exemption?.active && contract.exemption?.skipDGS)
      ? { required: false, olsRequired: false, otaRequired: false, threshold: 0, reason: 'DGS/OLS routing waived by statutory exemption (' + (contract.exemption.statute || 'see exemption details') + ').' }
      : (() => {
          const base = dgsCheck(contract, paal);
          if (base.otaRequired && contract.otaExemption?.active) {
            return {
              ...base,
              otaRequired: false,
              otaExempted: true,
              reason: base.reason + ' OTA exemption active: ' + (contract.otaExemption.basis || 'basis not yet selected — complete exemption fields below') + '.',
            };
          }
          return base;
        })()
    : null;
  const pendingReminders = (contract?.reminders || []).filter(r => !r.done).length;
  const profileComplete = !!(profile.contactName && profile.contactEmail && profile.agencyName);
  const isAgencyAdmin = profile.role === 'admin' || profile.role === 'agency_super_admin';
  const loadedLib = Object.keys(library).filter(k => library[k]);
  const loadedRef = loadedLib.filter(k => REF_DOCS.some(d => d.key === k)).length;
  const loadedSam = 0;
  const loadedTotal = loadedLib.length;
  const storageKb = store.getUsage ? store.getUsage().kb : 0;
  const todayStr = getTodayStr();

  function isAuto(field, profileVal) {
    return !!(s215[field] && profileVal && s215[field] === profileVal);
  }

  // ── Save helpers ──────────────────────────────────────────────────────
  const saveContract = useCallback((c) => {
    setAutosave('saving');
    pendingContractRef.current = c;
    if (contractSaveTimer.current) clearTimeout(contractSaveTimer.current);
    contractSaveTimer.current = setTimeout(async () => {
      await contractsDb.save(c);
      pendingContractRef.current = null;
      setAutosave('idle');
    }, 1500);
  }, []);

  const saveContractList = useCallback((list) => {
    if (contractListSaveTimer.current) clearTimeout(contractListSaveTimer.current);
    contractListSaveTimer.current = setTimeout(() => {}, 600);
  }, []);

  function flushSaves(contractToSave) {
    if (contractSaveTimer.current) {
      clearTimeout(contractSaveTimer.current);
      contractSaveTimer.current = null;
      const c = contractToSave || pendingContractRef.current || contract;
      if (c) contractsDb.save(c);
    }
  }

  // ── Contract mutations ────────────────────────────────────────────────
  const updateC = useCallback((updates, immediate = false) => {
    setContract(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...updates, updatedAt: today() };
      if (updates.s215) next.s215 = { ...prev.s215, ...updates.s215 };
      if (updates.pennyDrafts) next.pennyDrafts = { ...(prev.pennyDrafts || {}), ...updates.pennyDrafts };
      if (updates.bidEval) next.bidEval = { ...(prev.bidEval || {}), ...updates.bidEval };
      if (immediate) {
        if (contractSaveTimer.current) clearTimeout(contractSaveTimer.current);
        contractSaveTimer.current = null;
        pendingContractRef.current = null;
        contractsDb.save(next);
        setAutosave('idle');
      } else {
        saveContract(next);
      }
      setContracts(list => {
        const newList = list.map(e =>
          e.id === next.id ? buildContractListEntry(next) : e
        );
        saveContractList(newList);
        return newList;
      });
      return next;
    });
  }, [saveContract, saveContractList]);

  const updateS215 = useCallback((field, value) => {
    updateC({ s215: { [field]: value } });
  }, [updateC]);

  // ── Open/Create contract ──────────────────────────────────────────────
  async function openContract(id) {
    flushSaves();
    // Always fetch fresh from Supabase to ensure full data
    const saved = await contractsDb.get(id);
    const contractToOpen = saved || contracts.find(c => c.id === id);
    if (!contractToOpen) return;

    // Auto-apply profile defaults to empty s215 fields
    const s215Current = contractToOpen.s215 || {};
    const profileDefaults = {};
    const defaultFields = ['contactName', 'contactTitle', 'contactPhone', 'contactEmail',
      'accountingOfficerName', 'accountingOfficerTitle', 'accountingOfficerPhone', 'accountingOfficerEmail',
      'signatoryName', 'signatoryTitle', 'signatoryPhone', 'signatoryEmail',
      'signatoryAddress', 'signatoryCity', 'signatoryZip', 'agencyBillingCode'];
    defaultFields.forEach(f => { if (!s215Current[f] && profile[f]) profileDefaults[f] = profile[f]; });
    if (Object.keys(profileDefaults).length > 0) {
      contractToOpen.s215 = { ...s215Current, ...profileDefaults };
    }

    setContract(contractToOpen);
    setActiveId(id);
    setView('contract');
    setTab('stage');
  }

  async function createContract(formData) {
    const { requestDoc, cmasDoc, ...rest } = formData;
    const c = DEFAULT_CONTRACT(profile, paal);
    c.title = rest.title;
    c.description = rest.description;
    c.analystName = profile?.contactName || '';
    c.analystEmail = profile?.contactEmail || '';
    c.budget = rest.budget;
    c.isIT = rest.isIT;
    c.procMethod = rest.procMethod;
    c.category = rest.category;
    c.notes = rest.notes;
    if (rest.assignedCmId) c.assignedCmId = rest.assignedCmId;

    if (requestDoc?.base64) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const bytes = atob(requestDoc.base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          await supabase.storage.from('attachment-files')
            .upload(user.id + '/' + c.id + '/request-doc', blob, { upsert: true });
          c.attachments = [{
            id: 'request-doc', name: requestDoc.name,
            size: requestDoc.size, date: today(),
            tag: 'Request Document',
          }];
        }
      } catch (e) { console.error('Request doc save error:', e); }
    }

    if (cmasDoc?.base64) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const bytes = atob(cmasDoc.base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          const storagePath = user.id + '/' + c.id + '/cmas/' + cmasDoc.name.replace(/\s+/g, '_');
          const { error } = await supabase.storage.from('exhibit-files')
            .upload(storagePath, blob, { upsert: true });
          if (!error) {
            c.pennyDrafts = {
              ...c.pennyDrafts,
              cmasDoc: { fileName: cmasDoc.name, size: cmasDoc.size, storagePath, uploadedAt: new Date().toISOString() },
            };
          }
        }
      } catch (e) { console.error('CMAS doc save error:', e); }
    }

    await contractsDb.save(c);
    const entry = buildContractListEntry(c);
    setContracts(p => [entry, ...p]);
    setContract(c);
    setActiveId(c.id);
    setView('contract');
    setTab('stage');
    setShowNewModal(false);
    toast.success('Contract created');
  }

  // ── Profile & PAAL ────────────────────────────────────────────────────
  async function saveProfile(p) {
    setProfile(p);
    const ok = await profileDb.save(p);
    if (ok) toast.success('Profile saved');
    else toast.error('Failed to save profile');
  }

  async function savePaal(p) {
    setPaal(p);
    const ok = await paalDb.save(p);
    if (ok) toast.success('PAAL saved');
    else toast.error('Failed to save PAAL');
  }

  // ── STD 215 Defaults ──────────────────────────────────────────────────
  function applyDefaults() {
    const fills = {};
    const fields = ['contactName', 'contactTitle', 'contactPhone', 'contactEmail',
      'accountingOfficerName', 'accountingOfficerTitle', 'accountingOfficerPhone', 'accountingOfficerEmail',
      'signatoryName', 'signatoryTitle', 'signatoryPhone', 'signatoryEmail', 'signatoryAddress', 'signatoryCity', 'signatoryZip', 'agencyBillingCode'];
    fields.forEach(f => { if (!s215[f] && profile[f]) fills[f] = profile[f]; });
    if (Object.keys(fills).length) {
      updateC({ s215: fills });
      toast.success('Defaults applied');
    } else {
      toast.info('All fields already filled or no profile defaults set');
    }
  }

  function overwriteDefaults() {
    const fills = {};
    const fields = ['contactName', 'contactTitle', 'contactPhone', 'contactEmail',
      'accountingOfficerName', 'accountingOfficerTitle', 'accountingOfficerPhone', 'accountingOfficerEmail',
      'signatoryName', 'signatoryTitle', 'signatoryPhone', 'signatoryEmail', 'signatoryAddress', 'signatoryCity', 'signatoryZip', 'agencyBillingCode'];
    fields.forEach(f => { if (profile[f]) fills[f] = profile[f]; });
    updateC({ s215: fills });
    toast.success('All fields overwritten from profile');
  }

  // ── Stage advancement ─────────────────────────────────────────────────
  function advanceStage(targetStage, note) {
    const hist = [
      ...(contract.stageHistory || []),
      { stage: targetStage, date: today(), note: note || STAGES[targetStage]?.label || '' },
    ];
    const reminders = [...(contract.reminders || [])];

    if (targetStage === 10) {
      reminders.push({
        id: uid(), type: 'union', urgent: true, done: false,
        title: 'Union Notification — GC 19131',
        description: 'Send formal notification to exclusive representative within 10 business days.',
        due: '',
      });
    }
    if (targetStage === 13 && dgsResult?.required) {
      reminders.push({
        id: uid(), type: 'dgs', urgent: true, done: false,
        title: dgsResult?.otaRequired ? 'Route to DGS/OTA & DGS/OLS' : 'Route to DGS/OLS for Approval',
        description: dgsResult?.otaRequired
          ? 'Submit requisition to DGS/OTA to conduct solicitation, then route complete package to DGS/OLS for contract approval.'
          : 'Submit complete routing package to DGS/OLS. Contract cannot execute without approval.',
        due: '',
      });
    }
    if (targetStage === 14) {
      reminders.push({
        id: uid(), type: 'active', urgent: false, done: false,
        title: 'Contract Active — Monitor Deliverables',
        description: 'Track deliverable schedule and invoice milestones per Exhibit A.',
        due: '',
      });
    }

    updateC({ stage: targetStage, stageHistory: hist, reminders, ...(targetStage === 10 ? { pennyDrafts: { ...(contract.pennyDrafts || {}), unionData: { ...((contract.pennyDrafts || {}).unionData || {}), triggerFetch: true } } } : {}) });
    setShowStageModal(false);
    setPendingStage(null);
    toast.success('Advanced to ' + STAGES[targetStage]?.label);
  }

  function toggleReminder(id) {
    updateC({
      reminders: (contract.reminders || []).map(r =>
        r.id === id ? { ...r, done: !r.done } : r
      ),
    });
  }

  // ── Save to Vault ─────────────────────────────────────────────────────
  function saveDoc(type, content, vtype = 'draft', refs = []) {
    const docs = { ...(contract.documents || {}), [type]: [
      ...((contract.documents || {})[type] || []),
      {
        id: uid(), date: today(), type: vtype, content,
        label: (vtype === 'final' ? 'Final' : 'Draft') + ' — ' + fmtDate(today()),
        refs: refs.length > 0 ? refs : undefined,
      },
    ]};
    updateC({ documents: docs });
    toast.success('Saved to Vault');
  }

  async function signOut() {
    flushSaves();
    await supabase.auth.signOut();
    dataLoadedRef.current = false;
    setContract(null);
    setContracts([]);
    setProfile(DEFAULT_PROFILE);
    setCms([]);
    setTemplates([]);
    setLibrary({});
    setPaal(DEFAULT_PAAL);
    setBooting(true);
  }

  // ── Library ───────────────────────────────────────────────────────────
  async function uploadLibDoc(key, file) {
    setLibLoading(p => ({ ...p, [key]: true }));
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(',')[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const entry = { name: file.name, base64: b64, size: file.size, date: today() };
      setLibrary(p => ({ ...p, [key]: entry }));
      await libraryDb.set(key, entry);
      toast.success(file.name + ' added to library');
    } catch (e) {
      toast.error('Failed to upload: ' + e.message);
    } finally {
      setLibLoading(p => ({ ...p, [key]: false }));
    }
  }

  async function removeLibDoc(key) {
    setLibrary(p => { const n = { ...p }; delete n[key]; return n; });
    await libraryDb.del(key);
    toast.info('Document removed from library');
  }

  // ── Exhibits ──────────────────────────────────────────────────────────
  function addExhibit() {
    const label = nextExLabel(contract.exhibits || [], contract.exhibitNumbering);
    const exhibits = [...(contract.exhibits || []), { id: uid(), label, name: '', pages: '', description: '' }];
    updateC({ exhibits });
  }

  function updateExhibit(id, field, val) {
    updateC({ exhibits: (contract.exhibits || []).map(e => e.id === id ? { ...e, [field]: val } : e) });
  }

  function removeExhibit(id) {
    const remaining = (contract.exhibits || []).filter(e => e.id !== id);
    const reLabeled = contract.exhibitNumbering === 'alpha'
      ? remaining.map((e, i) => ({ ...e, label: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[i] }))
      : remaining.map((e, i) => ({ ...e, label: String(i + 1) }));
    updateC({ exhibits: reLabeled });
  }

  function moveExhibit(from, to) {
    if (to < 0 || to >= (contract.exhibits || []).length) return;
    const arr = [...contract.exhibits];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    const mode = contract.exhibitNumbering || 'alpha';
    const reLabeled = mode === 'alpha'
      ? arr.map((e, i) => ({ ...e, label: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[i] }))
      : arr.map((e, i) => ({ ...e, label: String(i + 1) }));
    updateC({ exhibits: reLabeled });
  }

  function changeNumbering(mode) {
    const exhibits = (contract.exhibits || []).map((e, i) => ({
      ...e, label: mode === 'alpha' ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[i] : String(i + 1),
    }));
    updateC({ exhibits, exhibitNumbering: mode });
  }

  function std213ExhibitText() {
    return (contract?.exhibits || [])
      .map(e => 'Exhibit ' + e.label + ' — ' + (e.name || '[TBD]') + (e.pages ? ' (' + e.pages + ' pages)' : ''))
      .join('\n');
  }

  // ── Attachments ───────────────────────────────────────────────────────
  async function uploadAttachment(file) {
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result.split(',')[1]);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const id = uid();
    const num = 'ATT-' + String((contract.attachments || []).length + 1).padStart(2, '0');
    const meta = {
      id, number: num,
      originalName: file.name,
      name: file.name.replace(/\.pdf$/i, ''),
      size: file.size,
      date: today(),
    };
    try {
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: file.type || 'application/octet-stream' });
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.storage.from('attachment-files')
          .upload(user.id + '/' + contract.id + '/' + id, blob, { upsert: true });
      }
    } catch (e) { console.error('Attachment upload error:', e); }
    updateC({ attachments: [...(contract.attachments || []), meta] });
    toast.success(file.name + ' uploaded');
  }

  async function removeAttachment(id) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.storage.from('attachment-files')
          .remove([user.id + '/' + contract.id + '/' + id]);
      }
    } catch (e) {}
    updateC({ attachments: (contract.attachments || []).filter(a => a.id !== id) });
  }

  // ── Amendments ────────────────────────────────────────────────────────
  function addAmendment(data) {
    const num = (contract.amendments || []).length + 1;
    const amendment = { id: uid(), number: num, ...data, date: data.date || today() };
    const s215Updates = {
      amendmentNum: data.agreementNum || '',
      maxAmount: data.newMaxAmount || s215.maxAmount,
    };
    const hist = [
      ...(contract.stageHistory || []),
      { stage: contract.stage, date: today(), note: 'Amendment ' + num + ' added: ' + data.description },
    ];
    updateC({
      amendments: [...(contract.amendments || []), amendment],
      s215: s215Updates,
      stageHistory: hist,
    });
    setShowAmendModal(false);
    toast.success('Amendment ' + num + ' added');
  }

  // ── Delete contract ───────────────────────────────────────────────────
  async function deleteContract() {
    if (!contract) return;
    await contractsDb.delete(contract.id);
    setContracts(p => p.filter(c => c.id !== contract.id));
    setContract(null);
    setActiveId(null);
    setView('dashboard');
    setShowDeleteConfirm(false);
    toast.success('Contract deleted');
  }

  // ── Import existing contract ──────────────────────────────────────────
  async function importExistingContract(files, notes, targetStage) {
    setImportLoading(true);
    try {
      const docs = [];
      for (const f of files) {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result.split(',')[1]);
          r.onerror = rej;
          r.readAsDataURL(f);
        });
        try { atob(b64); } catch (_) {}
        docs.push({ name: f.name, base64: b64 });
      }

      const sys = 'Extract key fields from this contract document. Return ONLY valid JSON with no markdown:\n{"title":"","budget":"","isIT":"yes|no","procMethod":"","agreementNum":"","contractorName":"","contractorAddress":"","startDate":"YYYY-MM-DD","endDate":"YYYY-MM-DD","maxAmount":"","description":""}';
      const dBlocks = docs.map(d => ({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: d.base64 } }));

      const raw = await callAI(apiKey, {
        system: sys,
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 500,
        messages: [{ role: 'user', content: [...dBlocks, { type: 'text', text: 'Extract fields. Context: ' + (notes || 'none') }] }],
        tag: 'contract_import',
      });

      let ex = {};
      try {
        const cleaned = raw.trim().replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '').trim();
        ex = JSON.parse(cleaned);
      } catch (_) {
        ex = { title: files[0]?.name?.replace(/\.pdf$/i, '') || 'Imported Contract' };
      }

      const c = DEFAULT_CONTRACT(profile, paal);
      c.title = ex.title || files[0]?.name?.replace(/\.pdf$/i, '') || 'Imported Contract';
      c.description = ex.description || '';
      c.budget = ex.budget || '';
      c.isIT = ex.isIT === 'yes' ? 'yes' : 'no';
      c.procMethod = ex.procMethod || '';
      c.stage = targetStage;
      c.stageHistory = Array.from({ length: targetStage + 1 }, (_, i) => ({
        stage: i, date: today(),
        note: i === targetStage ? 'Imported from PDF' : STAGES[i]?.label || '',
      }));
      c.s215 = {
        ...c.s215,
        agreementNum: ex.agreementNum || '',
        contractorName: ex.contractorName || '',
        contractorAddress: ex.contractorAddress || '',
        startDate: ex.startDate || '',
        endDate: ex.endDate || '',
        maxAmount: ex.maxAmount || ex.budget || '',
      };

      await contractsDb.save(c);
      const entry = buildContractListEntry(c);
      setContracts(p => [entry, ...p]);
      setContract(c);
      setActiveId(c.id);
      setView('contract');
      setTab('stage');
      setShowImportModal(false);
      toast.success('Contract imported: ' + c.title);
    } catch (e) {
      toast.error('Import failed: ' + e.message);
    } finally {
      setImportLoading(false);
    }
  }

  // ── Rendering ─────────────────────────────────────────────────────────
  if (booting) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--paper)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spin spin-lg spin-gold" style={{ margin: '0 auto 16px' }} />
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 16, color: 'var(--navy-600)' }}>Loading…</div>
        </div>
      </div>
    );
  }

  const tabProps = {
    contract, updateC, updateS215, s215, profile, paal, dgsResult, isAuto,
    cms, templates, assignedCm: cms.find(c => c.id === contract?.assignedCmId) || null,
    agencyDocTemplate,
    applyDefaults, overwriteDefaults, saveDoc,
    addExhibit, updateExhibit, removeExhibit, moveExhibit, changeNumbering, std213ExhibitText,
    uploadAttachment, removeAttachment,
    addAmendment: () => setShowAmendModal(true),
    setPendingStage, setShowStageModal,
    library, loadedRef, loadedSam, loadedTotal,
    apiKey, callAI, uploadToAnthropicFiles, todayStr, fmtDate, fmtMoney, fmtSize, toast,
    currentUserId,
    contractRole: contract ? (contract.ownerId === currentUserId ? 'owner' : memberRoles[contract.id] || 'viewer') : 'owner',
    isReadOnly: contract ? (contract.ownerId !== currentUserId && memberRoles[contract.id] === 'viewer') : false,
    toggleReminder,
    onCelebrate: () => setShowCelebration(true),
  };

  if (window.location.pathname === '/survey') return <SurveyPage />;
  if (!authReady) return null;
  if (!session) {
    if (window.location.pathname === '/') {
      window.location.replace('/landing');
      return null;
    }
    return <Auth />;
  }

  if (isSuperAdmin && superAdminView === 'dashboard') {
    return <SuperAdminDashboard onEnterApp={() => setSuperAdminView('app')} />;
  }

  if (!isSuperAdmin && userStatus && userStatus !== 'active') {
    return <PendingApproval status={userStatus} />;
  }

  // ── Route Contract Managers to their own experience ───────────────────
  if (profile.role === 'contract_manager') {
    if (showCMOnboarding) {
      return (
        <CMOnboardingFlow
          profile={profile}
          cmProfile={cmProfile}
          onComplete={() => setShowCMOnboarding(false)}
          onSignOut={signOut}
        />
      );
    }
    return (
      <CMDashboard
        profile={profile}
        cmProfile={cmProfile}
        apiKey={apiKey}
        callAI={callAI}
        onSignOut={signOut}
        onOpenDraft={(draft) => {
          // SOWDraftingModule wired in next phase
          alert('SOW Drafting workspace coming in the next phase! Draft: ' + (draft.title || draft.reference_number));
        }}
        onNewSOW={() => {
          // SOWDraftingModule wired in next phase
          alert('SOW Drafting workspace coming in the next phase!');
        }}
      />
    );
  }

  // ── Main analyst app ──────────────────────────────────────────────────
  return (
    <div className="app-layout">

      {/* ── Overlays ─────────────────────────────────────────────── */}
      {showAgencyAdmin && (
        <AgencyAdminPanel
          profile={profile}
          contracts={contracts}
          onClose={() => setShowAgencyAdmin(false)}
          toast={toast}
          apiKey={apiKey}
          callAI={callAI}
          templates={templates}
          uploadToAnthropicFiles={uploadToAnthropicFiles}
        />
      )}
      {showCelebration && contract && (
        <ContractCelebration
          contract={contract}
          s215={s215}
          profile={profile}
          apiKey={apiKey}
          callAI={callAI}
          onClose={() => setShowCelebration(false)}
        />
      )}
      {showOnboarding && (
        <OnboardingFlow
          profile={profile}
          paal={paal}
          library={library}
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => setShowOnboarding(false)}
          setView={v => { setView(v); setShowOnboarding(false); }}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        view={view} setView={setView}
        onSignOut={signOut}
        onAdminDashboard={isSuperAdmin ? () => setSuperAdminView('dashboard') : undefined}
        onAgencyAdmin={isAgencyAdmin && !isSuperAdmin ? () => setShowAgencyAdmin(true) : undefined}
        onShowOnboarding={() => setShowOnboarding(true)}
        contract={contract} contracts={contracts}
        tab={tab} setTab={setTab}
        activeId={activeId} openContract={openContract}
        flushSaves={flushSaves}
        pendingReminders={pendingReminders}
        loadedTotal={loadedTotal} library={library}
        paal={paal} profileComplete={profileComplete}
        darkMode={darkMode} setDarkMode={setDarkMode}
        storageKb={storageKb}
        cmsCount={cms.length}
        templatesCount={templates.length}
        isAdmin={isSuperAdmin}
      />

      {/* Main content */}
      <main className="main-area">
        <div className="main-scroll">
          <div className="main-inner">

            {view === 'dashboard' && (
              <Dashboard
                contracts={contracts}
                openContract={openContract}
                setShowNewModal={setShowNewModal}
                setShowImportModal={setShowImportModal}
                paal={paal} profile={profile}
              />
            )}

            {view === 'analytics' && (
              <AnalyticsView
                contracts={contracts} paal={paal} profile={profile}
                openContract={openContract} apiKey={apiKey} callAI={callAI}
                todayStr={todayStr}
              />
            )}

            {view === 'contract' && contract && (
              <div>
                <div className="page-header">
                  <div className="page-header-left">
                    <h1 className="page-title" style={{ fontSize: 22 }}>
                      {contract.title || 'Untitled Contract'}
                      {contract.contractNum && (
                        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--navy-500)', background: 'var(--slate-100)', padding: '2px 8px', borderRadius: 6, letterSpacing: '.03em' }}>
                          {contract.contractNum}
                        </span>
                      )}
                      <span className={`badge badge-gold`} style={{ fontSize: 10 }}>
                        {STAGES[contract.stage]?.label}
                      </span>
                      {contract.exemption?.active
                        ? <span className="badge badge-green" style={{ fontSize: 10 }}>✓ Exempt {contract.exemption.statute ? '· ' + contract.exemption.statute : ''}</span>
                        : dgsResult?.required && (
                          dgsResult.otaRequired && dgsResult.olsRequired
                            ? <><span className="badge badge-red" style={{ fontSize: 10 }}>DGS/OTA</span><span className="badge badge-orange" style={{ fontSize: 10, marginLeft: 4 }}>DGS/OLS</span></>
                            : <span className="badge badge-orange" style={{ fontSize: 10 }}>DGS/OLS</span>
                        )
                      }
                      {pendingReminders > 0 && <span className="badge badge-orange" style={{ fontSize: 10 }}>{pendingReminders} action{pendingReminders > 1 ? 's' : ''}</span>}
                    </h1>
                    <p className="page-subtitle">
                      {fmtMoney(contract.budget)}
                      {' · '}{contract.isIT === 'yes' ? 'IT — SCM Vol 2' : 'Non-IT — SCM Vol 1'}
                      {contract.procMethod ? ' · ' + contract.procMethod : ''}
                      {s215.contractorName ? ' · ' + s215.contractorName : ''}
                    </p>
                  </div>
                  <div className="page-header-actions">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--slate-400)' }}>
                      <div className={`autosave-dot ${autosave === 'saving' ? 'saving' : ''}`} />
                      {autosave === 'saving' ? 'Saving…' : 'Saved'}
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => { flushSaves(); setView('dashboard'); }}>
                      ← All Contracts
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
                      Delete
                    </button>
                  </div>
                </div>

                {contract.archived && (
                  <div style={{ padding: '8px 16px', background: 'var(--slate-100)', borderBottom: '1px solid var(--slate-200)', fontSize: 12.5, color: 'var(--slate-500)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>📁</span> This contract is archived and read-only. Archived {contract.archivedAt}.
                  </div>
                )}

                <div className="tabs-bar">
                  {[
                    ['stage',       'Stage & Info'],
                    ['draft',       'Penny'],
                    ...(contract.stage >= 14 || contract.archived ? [['handoff', '📁 Handoff']] : []),
                    ['sharing', '👥 Sharing'],
                    ...(contract.stage >= 8 ? [['bideval', '📊 Bid Eval']] : []),
                    ['assembly',    'Assembly' + (contract.exhibits?.length > 0 ? ` (${contract.exhibits.length})` : '')],
                    ['std213',      '📄 STD 213'],
                    ['std215',      '📋 STD 215'],
                    ['documents',   'Documents'],
                    ['amendments',  'Amendments' + (contract.amendments?.length > 0 ? ` (${contract.amendments.length})` : '')],
                    ['emails',      'Emails'],
                    ['vault',       'Vault'],
                    ['reminders',   'Reminders' + (pendingReminders > 0 ? ` (${pendingReminders})` : '')],
                  ].map(([t, lbl]) => (
                    <button key={t} className={`tab-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
                      {lbl}
                    </button>
                  ))}
                </div>

                {tabProps.isReadOnly && (
                  <div style={{ background: 'var(--blue-50)', border: '1px solid var(--blue-200)', borderRadius: 'var(--r-md)', padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--blue-700)' }}>
                    <span style={{ fontSize: 16 }}>👁</span>
                    <span><strong>View only</strong> — You have read access to this contract. Contact the owner to request edit access.</span>
                  </div>
                )}

                {tab === 'stage'       && <StageTab {...tabProps} />}
                {tab === 'draft'       && <DraftTab {...tabProps} onScmRefreshReady={fn => { generateScmContextRef.current = fn; }} />}
                {tab === 'handoff'     && <HandoffTab {...tabProps} />}
                {tab === 'sharing'     && <CollaborationPanel contract={contract} currentUserId={currentUserId} toast={toast} apiKey={apiKey} callAI={callAI} updateC={updateC} onAdvanceStage={(stage) => advanceStage(stage, 'Advanced from review approval')} />}
                {tab === 'bideval'     && <BidEvalTab {...tabProps} />}
                {tab === 'assembly'    && <AssemblyTab contract={contract} updateC={updateC} profile={profile} pennyDrafts={contract.pennyDrafts} toast={toast} setTab={setTab} />}
                {tab === 'std213'      && <STD213Tab contract={contract} updateC={updateC} s215={s215} profile={profile} paal={paal} apiKey={apiKey} callAI={callAI} />}
                {tab === 'std215'      && <STD215Tab contract={contract} updateC={updateC} updateS215={updateS215} s215={s215} profile={profile} paal={paal} onUpdateProfile={updates => saveProfile({ ...profile, ...updates })} />}
                {tab === 'documents'   && <DocumentsTab contract={contract} updateC={updateC} toast={toast} />}
                {tab === 'amendments'  && <AmendmentsTab {...tabProps} />}
                {tab === 'emails'      && <EmailsTab {...tabProps} />}
                {tab === 'vault'       && <VaultTab {...tabProps} />}
                {tab === 'reminders'   && <RemindersTab {...tabProps} />}
              </div>
            )}

            {view === 'profile'   && <ProfileView profile={profile} onSave={saveProfile} />}
            {view === 'paal'      && <PAALView paal={paal} onSave={savePaal} apiKey={apiKey} callAI={callAI} toast={toast} />}
            {view === 'library'   && <LibraryView library={library} />}
            {view === 'settings'  && <SettingsView profile={profile} contracts={contracts} store={store} />}
            {view === 'cms'       && <CMRosterView cms={cms} onUpdate={setCms} apiKey={apiKey} callAI={callAI} profile={profile} />}
            {view === 'templates' && <TemplateLibraryView templates={templates} onUpdate={setTemplates} toast={toast} profile={profile} apiKey={apiKey} callAI={callAI} uploadToAnthropicFiles={uploadToAnthropicFiles} />}
            {view === 'archive'   && <ArchiveView contracts={contracts} onOpen={c => openContract(c.id)} />}
            {view === 'supervisor'&& <SupervisorView contracts={contracts} paal={paal} profile={profile} onOpen={id => openContract(id)} />}
            {view === 'vendors'   && <VendorLibraryView profile={profile} toast={toast} apiKey={apiKey} callAI={callAI} />}

          </div>
        </div>
      </main>

      {contract && (
        <WhatNextPanel
          contract={contract} profile={profile} paal={paal}
          apiKey={apiKey} callAI={callAI} dgsResult={dgsResult}
          setView={setView} setTab={setTab}
          updateC={updateC} uploadToAnthropicFiles={uploadToAnthropicFiles}
          currentTab={tab}
          allContracts={contracts}
        />
      )}

      {showNewModal && (
        <NewContractModal onClose={() => setShowNewModal(false)} onCreate={createContract} profile={profile} cms={cms} apiKey={apiKey} callAI={callAI} />
      )}
      {showImportModal && (
        <ImportContractModal onClose={() => setShowImportModal(false)} onImport={importExistingContract} loading={importLoading} />
      )}
      {showStageModal && pendingStage !== null && contract && (
        <StageModal
          contract={contract} pendingStage={pendingStage}
          onConfirm={advanceStage} onClose={() => { setShowStageModal(false); setPendingStage(null); }}
          dgsResult={dgsResult}
        />
      )}
      {showDeleteConfirm && contract && (
        <DeleteConfirmModal contract={contract} onConfirm={deleteContract} onClose={() => setShowDeleteConfirm(false)} />
      )}
      {showAmendModal && contract && (
        <AddAmendmentModal contract={contract} onAdd={addAmendment} onClose={() => setShowAmendModal(false)} />
      )}

      <ToastContainer />
    </div>
  );
}

// ─── Ask Penny Panel ────────────────────────────────────────────────────
function WhatNextPanel({ contract, profile, paal, apiKey, callAI, dgsResult, setView, setTab, updateC, uploadToAnthropicFiles, currentTab, allContracts }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const dgs = dgsResult || dgsCheck(contract, paal);
  const s215 = contract.s215 || {};

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0 && !loading) {
      sendMessage('What should I do next on this contract? Give me a concise, specific action list.', true);
    }
  }, [open]); // eslint-disable-line

  async function sendMessage(text, auto = false) {
    if (loading) return;
    const userMsg = { role: 'user', content: text };
    if (!auto) setMessages(p => [...p, userMsg]);
    setLoading(true);
    setInput('');

    const exemption = contract.exemption;
    const ctx = [
      'Contract: ' + contract.title,
      'Stage: ' + (STAGES[contract.stage]?.label || ''),
      'Budget: ' + fmtMoney(contract.budget),
      'Type: ' + (contract.isIT === 'yes' ? 'IT — SCM Vol 2' : 'Non-IT — SCM Vol 1'),
      'Method: ' + (contract.procMethod || 'Not set'),
      'Contractor: ' + (s215.contractorName || 'Not yet awarded'),
      'Agreement No.: ' + (s215.agreementNum || 'Not assigned'),
      exemption?.active
        ? 'STATUTORY EXEMPTION ACTIVE: ' + (exemption.statute || 'See exemption details') +
          (exemption.description ? ' — ' + exemption.description : '') +
          (exemption.skipDGS ? ' | DGS routing WAIVED' : '') +
          (exemption.skipCompetitive ? ' | Competitive bidding WAIVED' : '') +
          (exemption.skipUnion ? ' | Union notification WAIVED' : '') +
          (exemption.skipProtest ? ' | Protest period WAIVED' : '')
        : 'DGS Required: ' + (dgs.required ? 'YES — ' + dgs.reason : 'No'),
      'Pending reminders: ' + (contract.reminders?.filter(r => !r.done).length || 0),
      'Open exhibits: ' + (contract.exhibits?.length || 0),
      'Amendments: ' + (contract.amendments?.length || 0),
    ].join('\n');

    const exemptionNote = contract.exemption?.active
      ? ' IMPORTANT: This contract operates under a statutory exemption (' + (contract.exemption.statute || 'see details') + ').' +
        (contract.exemption.skipDGS ? ' DGS/OLS routing is WAIVED — do NOT recommend DGS routing for this contract.' : '') +
        (contract.exemption.skipCompetitive ? ' Competitive bidding is WAIVED — do NOT recommend competitive solicitation.' : '') +
        ' Focus your recommendations on what is actually required under the exemption, not standard SCM requirements that have been waived.'
      : '';
    const hasDocuments = (contract.referenceDocs?.some(d => d.base64)) ||
      (contract.attachments?.some(a => a.tag === 'Request Document' && a.base64));
    const docNote = hasDocuments
      ? ' You have been provided the contract\'s uploaded documents (request document and/or reference PDFs) and can read and reference them directly in your responses.'
      : '';

    const contractorName = contract.s215?.contractorName;
    const pastContracts = contractorName && allContracts
      ? allContracts.filter(c =>
          c.id !== contract.id &&
          c.s215?.contractorName?.toLowerCase() === contractorName.toLowerCase()
        ).slice(0, 5)
      : [];
    const contractorMemory = pastContracts.length > 0
      ? '\n\nCONTRACTOR HISTORY — ' + contractorName + ' has worked with Energy Safety before:\n' +
        pastContracts.map(c =>
          '• ' + (c.title || 'Untitled') + ' | ' + (c.procMethod || '?') + ' | $' + (parseFloat(c.budget || 0).toLocaleString()) +
          ' | Stage: ' + (STAGES[c.stage]?.label || '?') + (c.archived ? ' (Closed)' : ' (Active)') +
          (c.s215?.agreementNum ? ' | Agreement: ' + c.s215.agreementNum : '')
        ).join('\n') +
        '\n\nUse this history to inform your recommendations — note any patterns, issues, or relevant context from past work with this contractor.'
      : '';

    const tabContextMap = {
      'stage':       'The analyst is currently viewing the Stage & Info tab — they may be asking about DGS routing, procurement method, contract details, or stage progression.',
      'draft':       'The analyst is currently on the Penny Drafting tab — they may be working on the SOW, solicitation, Exhibit B/D, SCM compliance review, or clarifying questions.',
      'bideval':     'The analyst is currently on the Bid Evaluation tab — they may be asking about scoring criteria, weighted evaluation, bidder rankings, or award documentation requirements.',
      'assembly':    'The analyst is currently on the Assembly tab — they may be asking about exhibit structure, nested attachments, export presets, or the STD 213 exhibit listing.',
      'documents':   'The analyst is currently on the Documents tab — they may be asking about uploaded files, supporting documents, or the contract file library.',
      'amendments':  'The analyst is currently on the Amendments tab — they may be asking about amendment procedures, budget changes, or re-routing requirements.',
      'emails':      'The analyst is currently on the Emails tab — they may be asking about required procurement notifications, notices, or correspondence.',
      'vault':       'The analyst is currently on the Vault tab — they may be asking about document management or version control.',
      'reminders':   'The analyst is currently on the Reminders tab — they may be asking about upcoming deadlines or required actions.',
      'handoff':     'The analyst is currently on the Handoff & Closure tab — they may be asking about contract closeout procedures, handoff package requirements, or archive process.',
    };
    const tabContext = tabContextMap[currentTab] || '';

    const system = 'You are Penny, the AI contracting assistant built into GoldenScope — a contract management platform for California state procurement teams. You are currently assisting a contract specialist at the Office of Energy Infrastructure Safety (Energy Safety), Admin Services Division. ' +
      'You advise on contracts governed by the State Contracting Manual (SCM) Vol 1 (Non-IT) and Vol 2 (IT), Public Contract Code, and Government Code. ' +
      (tabContext ? 'Current context: ' + tabContext + '\n\n' : '') +
      'Contract details:\n' + ctx + '\n\n' +
      'Be specific and actionable. Reference SCM sections when relevant. Flag risks. Keep responses concise — 2-4 short paragraphs max unless detail is specifically requested.' +
      exemptionNote + docNote + contractorMemory;

    try {
      const docBlocks = [];
      const refreshedDocs = [];

      const addDocBlock = async (doc, name) => {
        try {
          const result = await getValidFileId(apiKey, doc);
          if (!result) return;
          docBlocks.push({ type: 'document', source: { type: 'file', file_id: result.fileId } });
          if (result.refreshed) {
            refreshedDocs.push({ doc, fileId: result.fileId, uploadedAt: result.uploadedAt });
          }
        } catch (e) {
          console.warn('Could not upload "' + (name || doc.name) + '" to Files API:', e.message);
          if (doc.base64) {
            docBlocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: doc.base64 } });
          }
        }
      };

      const reqDoc = (contract.attachments || []).find(a => a.tag === 'Request Document');
      if (reqDoc) await addDocBlock(reqDoc, 'Request Document');

      for (const doc of (contract.referenceDocs || [])) {
        await addDocBlock(doc, doc.name);
      }

      if (refreshedDocs.length > 0) {
        const updatedRefs = (contract.referenceDocs || []).map(d => {
          const refreshed = refreshedDocs.find(r => r.doc.id === d.id);
          return refreshed ? { ...d, anthropicFileId: refreshed.fileId, anthropicFileUploadedAt: refreshed.uploadedAt } : d;
        });
        updateC({ referenceDocs: updatedRefs });
      }

      const buildContent = (textContent) => {
        if (docBlocks.length > 0) {
          return [...docBlocks, { type: 'text', text: textContent }];
        }
        return textContent;
      };

      const allMsgs = [...messages, userMsg].filter(m => m.content !== text || !auto);
      const msgsWithDocs = allMsgs.map((m, i) => {
        if (m.role === 'user' && i === 0 && docBlocks.length > 0) {
          return { role: 'user', content: buildContent(typeof m.content === 'string' ? m.content : text) };
        }
        return m;
      });

      const aiText = await callAI(apiKey, {
        system,
        messages: auto
          ? [{ role: 'user', content: buildContent(text) }]
          : (msgsWithDocs.length > 0 ? msgsWithDocs : [{ role: 'user', content: buildContent(text) }]),
        model: 'claude-sonnet-4-6',
        max_tokens: 800,
        tag: 'ask_penny_chat',
        contract_id: contract?.id || null,
      });
      setMessages(p => [...p, { role: 'assistant', content: aiText }]);
    } catch (e) {
      setMessages(p => [...p, { role: 'assistant', content: '⚠ ' + e.message }]);
    } finally {
      setLoading(false);
    }
  }

  const quickBtns = [
    'What should I do next?',
    'What is the biggest risk?',
    'What documents am I missing?',
    'Walk me through the next step',
    'What would DGS flag on this?',
    'Write a quick status update',
    'Am I ready to post to CalEProcure?',
  ];

  return (
    <>
      {!open && (
        <button className="wnext-trigger" onClick={() => setOpen(true)}>
          <span>✒</span>
          Ask Penny
          <div className="wnext-notif" />
        </button>
      )}

      <div className={`wnext-panel ${open ? 'open' : ''}`}>
        <div className="wnext-header">
          <div>
            <div className="wnext-title">✒ Penny</div>
            <div className="wnext-subtitle">Your contract writing assistant · {(contract.title || 'Contract').slice(0, 28)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setMessages([]); setTimeout(() => sendMessage('What should I do next on this contract?', true), 100); }}
              style={{ background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 6, color: 'var(--navy-300)', cursor: 'pointer', padding: '4px 8px', fontSize: 10 }}>
              Refresh
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy-400)', fontSize: 16, display: 'flex', alignItems: 'center' }}>
              ×
            </button>
          </div>
        </div>

        <div className="wnext-ctx">
          <span>Stage {contract.stage + 1}: {STAGES[contract.stage]?.label}</span>
          <button
            onClick={() => { setOpen(false); setView('contract'); setTab('stage'); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gold-400)', fontSize: 10 }}>
            Open contract →
          </button>
        </div>

        <div className="wnext-messages">
          {messages.length === 0 && !loading && (
            <div style={{ color: 'var(--navy-400)', fontSize: 12, fontStyle: 'italic', textAlign: 'center', paddingTop: 20 }}>
              Loading recommendations…
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`wm-bubble ${m.role}`}>{m.content}</div>
          ))}
          {loading && (
            <div className="wm-thinking">
              <div className="wm-dot" /><div className="wm-dot" /><div className="wm-dot" />
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="wnext-quick-btns">
          {quickBtns.map(q => (
            <button key={q} className="btn btn-xs btn-ghost"
              style={{ fontSize: 10, color: 'var(--navy-300)', borderColor: 'rgba(255,255,255,.08)', marginBottom: 4 }}
              onClick={() => sendMessage(q)}>
              {q}
            </button>
          ))}
        </div>

        <div className="wnext-input-row">
          <input
            className="wnext-input"
            placeholder="Ask Penny anything about this contract…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && input.trim()) sendMessage(input); }}
            disabled={loading}
          />
          <button className="wnext-send" disabled={!input.trim() || loading} onClick={() => sendMessage(input)}>
            ➤
          </button>
        </div>
      </div>

      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 140 }} onClick={() => setOpen(false)} />}
    </>
  );
}
